import { HttpClient } from '@angular/common/http';
import { catchError, finalize, Observable, tap } from 'rxjs';
import { IAppConfig } from '../../../../app/app.config';
import { WebUser } from '../../../common/model/webUser';
import { FilteredCommand } from '../../model/command';
import { ModelBase } from '../../model/model-base';
import { MessageService } from '../message.service';
import { ProgressBarService } from '../progress-bar.service';
import { DataService } from './data.service';

export declare interface IAssignable {
  assign(command: FilteredCommand): Observable<WebUser>;
}

export declare interface IUnAssignable {
  unassign(command: FilteredCommand): Observable<void>;
}

export abstract class DataServiceErrand<TModel extends ModelBase> extends DataService<TModel> implements IAssignable, IUnAssignable {
  protected abstract readonly assignMessage: string;
  protected abstract readonly unassignMessage: string;

  constructor(
    client: HttpClient,
    config: IAppConfig,
    notification: MessageService,
    modelConstructor: { new (): TModel },
    progressBar: ProgressBarService,
    serviceEndPoint: string
  ) {
    super(client, config, notification, modelConstructor, progressBar, serviceEndPoint);
  }

  assign(command: FilteredCommand): Observable<WebUser> {
    this.progressBar.start();
    return this.client
      .put(`${this.endpoint}/assign`, {
        filter: this.prepareFilter(command.filterProvider.filterState),
        includedIds: command.includedIds,
        excludedIds: command.excludedIds,
        affectedItemsCount: command.totalAffectedItemsControlValue,
      })
      .pipe(
        tap(() => this.notification.showSuccess(this.assignMessage)),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  unassign(command: FilteredCommand): Observable<void> {
    this.progressBar.start();
    return this.client
      .put(`${this.endpoint}/unassign`, {
        filter: this.prepareFilter(command.filterProvider.filterState),
        includedIds: command.includedIds,
        excludedIds: command.excludedIds,
        affectedItemsCount: command.totalAffectedItemsControlValue,
      })
      .pipe(
        tap(() => this.notification.showSuccess(this.unassignMessage)),
        catchError(error => {
          this.notification.showError(error);
          return [];
        }),
        finalize(() => this.progressBar.stop())
      );
  }

  cancel(id: string) {
    this.progressBar.start();
    return this.client.put(`${this.endpoint}/cancel`, JSON.stringify(id)).pipe(
      catchError(error => {
        this.notification.showError(error);
        return [];
      }),
      finalize(() => this.progressBar.stop())
    );
  }
}
